import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/synapse/Icon';

interface PaginationFooterProps {
  /** 1-based. */
  page: number;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

/**
 * The footer strip under a paged list: which rows are on screen, and the way to
 * the next ones.
 *
 * One implementation rather than one per page. It had grown three copies —
 * stock, audit and issuing — which is how "Showing 1–25 of 60" ends up phrased
 * one way on two screens and another way on the third.
 *
 * Renders nothing when everything fits on one page: a disabled pair of arrows
 * under a list of four rows is chrome about a decision the user never has to
 * make.
 *
 * That test is `totalPages`, not a page size this component is told separately.
 * Every caller derives `totalPages` as `max(1, ceil(total / pageSize))`, which
 * makes the two questions the same question — and a `pageSize` prop that could
 * disagree with the offsets the caller is actually paging by is a way for the
 * footer to hide itself over a list with a second page.
 */
export function PaginationFooter({
  page,
  totalPages,
  rangeStart,
  rangeEnd,
  total,
  onPrev,
  onNext,
}: PaginationFooterProps) {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-t border-line bg-paper-2 px-4 py-2.5">
      <span className="text-[12px] tabular-nums text-muted">
        {t('stock.showingRange', { start: rangeStart, end: rangeEnd, total })}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onPrev} disabled={page <= 1}>
          <Icon name="chevron_left" size={14} /> {t('stock.prev')}
        </Button>
        <span className="text-[11.5px] tabular-nums text-muted">
          {t('stock.pageOf', { page, totalPages })}
        </span>
        <Button variant="ghost" size="sm" onClick={onNext} disabled={page >= totalPages}>
          {t('stock.next')} <Icon name="chevron_right" size={14} />
        </Button>
      </div>
    </div>
  );
}
